import React, { useState, useEffect } from "react";
import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Select,
  Col,
  Row,
  Popconfirm,
  Space,
  DatePicker,
} from "antd";
import { ReactComponent as DeleteIcon } from "../../../../../assets/delete-icon.svg";
import { ReactComponent as CrossIcon } from "../../../../../assets/crossIcon.svg";
import { useSelector } from "react-redux";
import moment from "moment";

const { Option } = Select;

const CustomFieldsComponent = ({
  inputFields,
  setInputFields,
  createField,
  deleteField,
  updateField,
  canUpdate,
  selectedTemplate,
  setSelectedTemplate,
  handleSelectTemplate,
  dynamicFieldsOptions,
  setDynamicFieldsOptions,
  project_id,
}) => {
  const { allTemplates } = useSelector((state) => state.materialTemplatesReducer);
  
  // Filter templates by projectId
  useEffect(() => {
    const filteredTemplates = allTemplates.filter(template => template.projectId === parseInt(project_id));
    // Check if selectedTemplate is empty or doesn't have fields
    if (filteredTemplates.length > 0 && 
        (!selectedTemplate || 
         Object.keys(selectedTemplate).length === 0 || 
         !selectedTemplate.fields)) {
      setSelectedTemplate(filteredTemplates[0]);
    }
  }, [allTemplates, project_id, selectedTemplate, setSelectedTemplate]);
  
  const handleDeleteLocally = (item) => {
    const filteredInputs = inputFields.filter((el) => el.id !== item.id);
    setInputFields(filteredInputs);
  };

  const addChooseField = () => {
    let field = {
      isEdit: true,
      field_type: "select",
      name: "",
      isLocal: true,
      id: Math.floor(Math.random() * 10000000),
      label: "Select Field",
      placeholder: `Select ${selectedTemplate?.name || 'Template'} Field`,
      isSelect: true,
    };
    setInputFields([...inputFields, { ...field }]);
  };

  const handleChangeField = (value, ele, option) => {
    const { index } = ele;
    // Find the complete field information from selectedTemplate
    const selectedField = selectedTemplate?.fields?.find((field, idx) => {
      return (field.id === value || idx === value);
    });
    
    if (!selectedField) return;
    
    // Get the field name from the selected field
    const fieldName = selectedField.name || '';
    
    // Create updated field with necessary properties for createField
    const updatedField = {
      ...ele,
      id: value,
      value,
      option: {
        ...option,
        field_type: selectedField.type || option.field_type,
        field_name: fieldName,
        field_index: value,
        selectedField: selectedField,
        children: fieldName // This is what createField looks for
      },
      label: fieldName,
      placeholder: fieldName,
      isSelect: false, // Convert to input field immediately
      name: '' // Initialize with empty name
    };
    
    // Update the inputFields state with the converted field
    const updatedFields = [...inputFields];
    updatedFields[index] = updatedField;
    setInputFields(updatedFields);
  };

  const handleDisable = (id) => {
    let item = inputFields.filter((el) => el.id === id && !el.isSelect);

    let shuoldDisabled = item.length > 0 ? true : false;

    return shuoldDisabled;
  };

  const SelectOption = ({ placeholder, ele }) => (
    <Select
      style={{ display: "block" }}
      placeholder="Select Custom Templates Fields"
      size="small"
      getPopupContainer={() =>
        document.getElementsByClassName(
          "ant-layout-content layout-content site-layout-background custom-textarea"
        )[0]
      }
      disabled={!canUpdate}
      onChange={(e, option) => handleChangeField(e, ele, option)}
      value={ele?.value !== undefined ? ele.value : null}
    >
      {selectedTemplate?.fields?.map((field, index) => {
        // Create a unique id for each field based on index if not present
        const id = field.id || index;
        const { name, type, options } = field;
        return (
          <Option
            value={id}
            key={id}
            disabled={handleDisable(id)}
            field_type={type}
            field_options={options || []}
          >
            {name}
          </Option>
        );
      })}
    </Select>
  );

  const RenderFieldTypes = ({ item, localName, setLocalName }) => {
    const { placeholder, option, index } = item;

    if (option.field_type === "date" || option.field_type === "date_field") {
      return (
        <DatePicker
          format={"DD MMM YYYY"}
          size="small"
          allowClear={false}
          suffixIcon={null}
          placeholder={"Select Date"}
          value={localName}
          disabled={!canUpdate}
          onChange={(e) => {
            setLocalName(moment(e));
            inputFields[index].name = moment(e);
            setInputFields(inputFields);
            // Auto-save on change for date picker
            updateField({ ...item, name: moment(e) });
          }}
        />
      );
    }

    if (option.field_type === "selector" || option.field_type === "select_field") {
      const field_options = option.options || option.field_options || [];
      console.log("placeholder", placeholder);
      return (
        <Select
          placeholder={`Select ${placeholder}`}
          size="small"
          disabled={!canUpdate}
          value={localName || undefined}
          allowClear
          showSearch={false}
          onChange={(e) => {
            setLocalName(e);
            // Auto-save on change for select
            updateField({ ...item, name: e });
          }}
        >
          <Option value="" disabled>{`Select ${placeholder}`}</Option>
          {field_options.map((el, idx) => {
            const id = el.id || idx;
            const name = el.name || el;
            return (
              <Option value={id} key={id} name={name}>
                {name}
              </Option>
            );
          })}
        </Select>
      );
    }

    return (
      <Input
        placeholder={placeholder}
        value={localName}
        disabled={!canUpdate}
        onChange={(e) => {
          const { value } = e.target;
          setLocalName(value);
          inputFields[index].name = value;
          setInputFields(inputFields);
        }}
        onBlur={() => {
          // Auto-save on blur for text input
          updateField({ ...item, name: localName });
        }}
      />
    );
  };

  const getSelectName = (options, id) => {
    // Since id is actually the index, find the option at that index
    let option = options[id];
    return option?.name || (typeof option === 'string' ? option : "");
  };

  const SelectedInput = ({ item }) => {
    const { name, option } = item;

    const value =
      option.field_type === "date" || option.field_type === "date_field"
        ? moment(name).format("DD MMM YYYY")
        : option.field_type === "selector" || option.field_type === "select_field"
        ? getSelectName(option.options || option.field_options || [], name)
        : name;

    return <Input className="border-less-input" readOnly value={value} />;
  };

  const RenderInputs = ({ ele }) => {
    const { isEdit, label, name } = ele;
    const [localName, setLocalName] = useState(name);

    return (
      <>
        <Row
          align="bottom"
          justify="start"
          className="field-row"
          gutter={[15, 0]}
        >
          <Col span={18}>
            <label>{label}</label>

            {ele?.isEdit ? (
              ele?.isSelect ? (
                <SelectOption ele={ele} />
              ) : (
                <RenderFieldTypes
                  item={ele}
                  localName={localName}
                  setLocalName={setLocalName}
                />
              )
            ) : (
              <SelectedInput item={ele} />
            )}
          </Col>
          {canUpdate && (
            <Col>
              <Space size={10} align="baseline">
                <Popconfirm
                  title={isEdit ? "Are you sure you want to cancel?" : "Are you sure you want to delete?"}
                  placement="topRight"
                  onConfirm={() => isEdit ? handleDeleteLocally(ele) : deleteField(ele)}
                  okText="Yes"
                  cancelText="No"
                >
                  {ele?.isSelect ? (
                    <CrossIcon />
                  ) : (
                    <DeleteIcon width={18} height={18} fill="#e21c21" />
                  )}
                </Popconfirm>
              </Space>
            </Col>
          )}
        </Row>
      </>
    );
  };

  return (
    <Row gutter={[20, 0]} className="custom-fields-parent-div">
      {inputFields.map((el, i) => {
        return (
          <Col span={24} key={`item-${el.id}`} index={i}>
            <RenderInputs ele={{ ...el, index: i }} />
          </Col>
        );
      })}
      {canUpdate && (
        <Col span={24}>
          <Button
            onClick={addChooseField}
            icon={<PlusOutlined />}
            size="small"
            className="add-field-btn"
          >
            Filter
          </Button>
        </Col>
      )}
    </Row>
  );
};

export default CustomFieldsComponent;
